import React, { Fragment, memo, useMemo } from 'react';

import { checkPermission } from 'components/PermissionHide/checkPermission';
import useGetPermissions from 'v2/hooks/useGetPermissions';
import useGetUser from 'v2/hooks/useGetUser';

interface IProps {
  action?: string | string[];
  inverse?: boolean;
  userTypes?: string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
  allowWithoutPermission?: boolean;
}

const PermissionHide = memo(
  ({ action, inverse, userTypes = [], children, fallback, allowWithoutPermission }: IProps) => {
    const user = useGetUser();
    const permissions = useGetPermissions();

    const canAccess = useMemo(() => {
      if (!permissions) {
        return false;
      }

      return allowWithoutPermission || checkPermission(permissions, action);
    }, [permissions, allowWithoutPermission, action]);

    const userCanViewByType = useMemo(() => {
      if (!userTypes.length) {
        return true;
      }

      if (!user) {
        return false;
      }

      const isProducer = user.producer;
      const isAffiliate = user.affiliate;
      const isCustomer = user.beta_customer;
      const isPartner = user.general_term;
      const isOnlyCustomer = !user.producer && !user.affiliate && !isPartner;
      const isOnlyProducer = user.producer && !user.affiliate;
      const isOnlyAffiliate = !user.producer && user.affiliate;

      if (userTypes.includes('producer') && isPartner) {
        return true;
      }

      if (userTypes.includes('affiliate') && isPartner) {
        return true;
      }

      if (userTypes.includes('onlyProducer') && isPartner) {
        return true;
      }

      if (userTypes.includes('onlyAffiliate') && isPartner) {
        return true;
      }

      if (userTypes.includes('producer') && isProducer) {
        return true;
      }

      if (userTypes.includes('affiliate') && isAffiliate) {
        return true;
      }

      if (userTypes.includes('customer') && isCustomer) {
        return true;
      }

      if (userTypes.includes('onlyCustomer') && isOnlyCustomer) {
        return true;
      }

      if (userTypes.includes('onlyProducer') && isOnlyProducer) {
        return true;
      }

      if (userTypes.includes('onlyAffiliate') && isOnlyAffiliate) {
        return true;
      }

      return false;
    }, [user, userTypes]);

    const renderChildren = useMemo(() => {
      if (!user || !permissions) {
        return null;
      }

      if (permissions.owned && inverse === true) {
        return children;
      }

      if (permissions.owned && inverse === false) {
        return null;
      }

      if (canAccess && !inverse) {
        return children;
      }

      if (canAccess && inverse) {
        return null;
      }

      if (!canAccess && inverse) {
        return children;
      }

      if (!userCanViewByType || !canAccess) {
        return null;
      }

      return children;
    }, [user, permissions, canAccess, inverse, userCanViewByType, children]);

    const shouldRenderFallback = useMemo(() => {
      if (renderChildren) {
        return renderChildren;
      }

      if (fallback && user && permissions && inverse && permissions.owned) {
        return children;
      }

      if (fallback && user && permissions) {
        return fallback;
      }

      return null;
    }, [fallback, renderChildren, user, permissions, inverse, children]);

    return <Fragment>{shouldRenderFallback}</Fragment>;
  }
);

export default PermissionHide;
